import React, { useEffect, useState } from "react";
import { Table, Form } from "react-bootstrap";
import DateRangePicker from "react-bootstrap-daterangepicker";
import "bootstrap-daterangepicker/daterangepicker.css";
import { Helmet } from "react-helmet";
import ContentWrapper from "../../components/contentwrapper/ContentWrapper";
import ReportNavBar from "../../components/ReportNavBar";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import { filterByParams } from "../../utils/api";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import CsvDownloader from "react-csv-downloader";
import { CiExport } from "react-icons/ci";
import withAuth from "../../utils/withAuth";
import { PieChart, Pie, Cell, Tooltip } from "recharts";

const DONUT_COLORS = [
    "#4285F4",
    "#FBBC05",
    "#34A853",
    "#EA4335",
    "#A142F4",
    "#00ACC1",
    "#F4511E",
    "#7CB342",
    "#FDD835",
    "#8D6E63",
];

const RADIAN = Math.PI / 180;

// label with line + product name + %
const renderDonutLabelWithLine = (props) => {
    const {
        cx,
        cy,
        midAngle,
        outerRadius,
        value,
        fill,
        payload,
    } = props;

    const sin = Math.sin(-RADIAN * midAngle);
    const cos = Math.cos(-RADIAN * midAngle);
    const labelText = `${payload.name} (${Number(value).toFixed(1)}%)`;

    const sx = cx + outerRadius * cos;
    const sy = cy + outerRadius * sin;

    const mx = cx + (outerRadius + 10) * cos;
    const my = cy + (outerRadius + 10) * sin;

    const ex = mx + (cos >= 0 ? 24 : -24);
    const ey = my;

    const textAnchor = cos >= 0 ? "start" : "end";

    return (
        <g>
            <polyline
                points={`${sx},${sy} ${mx},${my} ${ex},${ey}`}
                stroke={fill || "#999"}
                strokeWidth={1}
                fill="none"
            />
            <circle cx={ex} cy={ey} r={2} fill={fill || "#999"} />
            <text
                x={ex + (cos >= 0 ? 4 : -4)}
                y={ey}
                textAnchor={textAnchor}
                dominantBaseline="central"
                style={{
                    fontSize: "11px",
                    fontWeight: 600,
                    fill: "#333",
                }}
            >
                {labelText}
            </text>
        </g>
    );
};

const AllProductReport = () => {
    const [products, setProducts] = useState([]);         // table data
    const [totals, setTotals] = useState(null);          // optional totals
    const [topLimit, setTopLimit] = useState(20);
    const [isLoading, setIsLoading] = useState(false);

    const [dateRange, setDateRange] = useState({
        startDate: moment().startOf("month").format("YYYY-MM-DD"),
        endDate: moment().format("YYYY-MM-DD"),
    });

    const adminid = localStorage.getItem("admindata");
    const navigate = useNavigate();

    // -------- API CALL ----------
    const loadReport = async (options = {}) => {
        setIsLoading(true);

        const payload = {
            limit: options.limit ?? topLimit ?? 20,
            startDate: options.startDate ?? dateRange.startDate,
            endDate: options.endDate ?? dateRange.endDate,
        };

        try {
            const res = await filterByParams(
                "super-admin-get-all-product-performance-last-month",
                payload
            );

            if (res.data.status === 1) {
                const data = res.data;
                setProducts(data.products || []);
                setTotals(data.totals || null);

                setDateRange({
                    startDate: data.start_date || payload.startDate,
                    endDate: data.end_date || payload.endDate,
                });
            }
        } catch (err) {
            console.error("Error fetching product performance report:", err);
        }

        setIsLoading(false);
    };

    // initial load
    useEffect(() => {
        if (!adminid) {
            navigate("/");
        } else {
            loadReport();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [adminid]);

    // handlers
    const handleLimitChange = async (e) => {
        const newLimit = Number(e.target.value);
        setTopLimit(newLimit);
        await loadReport({ limit: newLimit });
    };

    const handleDateRangeApply = async (_event, picker) => {
        const selectedRange = {
            startDate: picker.startDate.format("YYYY-MM-DD"),
            endDate: picker.endDate.format("YYYY-MM-DD"),
        };
        setDateRange(selectedRange);
        await loadReport(selectedRange);
    };

    // -------- EXPORT CONFIG ----------
    const CATEGORY_LABELS = {
        1: "Ayurvedic",
        2: "Astrology",
    };

    const exportColumns = [
        { id: "p_name", displayName: "Product" },
        { id: "p_cat", displayName: "Category" },
        { id: "total_orders", displayName: "Total Orders" },
        { id: "total_qty", displayName: "Total Qty" },
        { id: "total_revenue", displayName: "Order Value" },
        { id: "delivered_revenue", displayName: "Delivered Revenue" },
        { id: "order_percent", displayName: "Order %" },
        { id: "delivery_percent", displayName: "Delivery %" },
        { id: "avg_ticket", displayName: "Avg Ticket" },
    ];

    const exportData = products.map((p) => ({
        p_name: p.p_name || "",
        p_cat: CATEGORY_LABELS[Number(p.p_catid)] || p.p_catid || "",
        total_orders: p.total_orders || 0,
        total_qty: p.total_qty || 0,
        total_revenue: p.total_revenue || 0,
        delivered_revenue: p.delivered_revenue || 0,
        order_percent: p.order_percent || 0,
        delivery_percent: p.delivery_percent || 0,
        avg_ticket: p.avg_ticket || 0,
    }));

    // -------- DONUT DATA (Order share %) ----------
    const donutData = (products || [])
        .map((p) => ({
            name: p.p_name || "NA",
            value: Number(p.delivery_percent || 0),
        }))
        .filter((d) => d.value > 0)
        .slice(0, 15); // max 20 slices

    return (
        <div className="top-products-state-report misDashboard">
            <Helmet>
                <title>SATKARTAR: All Product Performance</title>
            </Helmet>

            <ContentWrapper>
                <ReportNavBar />



                <div className="row" style={{ marginBottom: "50px" }}>
                    {/* LEFT: TABLE */}
                    <div className="col-lg-7 mb-3">
                        <div className="card h-100 shadow-sm border-0">
                            <div className="card-header bg-white border-0 d-flex justify-content-between align-items-center">
                                <h6
                                    className="mb-0 text-uppercase"
                                    style={{ letterSpacing: "0.05em" }}
                                >
                                    All Products Performance
                                </h6>
                                {/* Date range picker */}
                                <div className="d-flex justify-content-end mb-3">
                                    <DateRangePicker
                                        initialSettings={{
                                            startDate: moment(dateRange.startDate, "YYYY-MM-DD"),
                                            endDate: moment(dateRange.endDate, "YYYY-MM-DD"),
                                        }}
                                        onApply={handleDateRangeApply}
                                    >
                                        <button className="btn btn-sm btndaterange">
                                            {moment(dateRange.startDate, "YYYY-MM-DD").format("DD MMM YYYY")} –{" "}
                                            {moment(dateRange.endDate, "YYYY-MM-DD").format("DD MMM YYYY")}
                                        </button>
                                    </DateRangePicker>
                                </div>
                                <div className="d-flex align-items-center gap-2">
                                    <CsvDownloader
                                        filename={`ALL_PRODUCTS_PERFORMANCE_${moment().format(
                                            "YYYY_MM_DD"
                                        )}`}
                                        extension=".csv"
                                        columns={exportColumns}
                                        datas={exportData}
                                    >
                                        <button
                                            className="btn btn-xs d-flex align-items-center gap-1"
                                            style={{ background: "#0dcaf0", color: "#fff" }}
                                        >
                                            <CiExport />
                                            <span>Export</span>
                                        </button>
                                    </CsvDownloader>

                                    <Form.Select
                                        size="sm"
                                        style={{ width: 110 }}
                                        value={topLimit}
                                        onChange={handleLimitChange}
                                    >
                                        <option value={10}>Top 10</option>
                                        <option value={20}>Top 20</option>
                                        <option value={50}>Top 50</option>
                                        <option value={100}>Top 100</option>
                                    </Form.Select>
                                </div>
                            </div>

                            <div className="card-body p-0">
                                <Table
                                    className="table-sm table-fixeds"
                                    responsive
                                    striped
                                    bordered
                                    hover
                                >
                                    <thead className="table-light">
                                        <tr>
                                            <th style={{ width: 40 }}>#</th>
                                            <th>Product</th>
                                            <th>Cat</th>
                                            <th>Orders</th>
                                            <th>Qty</th>
                                            <th>Order Value</th>
                                            <th>Delivered Rev.</th>
                                            <th>Order %</th>
                                            <th>Delivery %</th>
                                            <th>Avg Ticket</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {isLoading ? (
                                            [...Array(5)].map((_, rowIdx) => (
                                                <tr key={rowIdx}>
                                                    {Array.from({ length: 10 }).map((__, colIdx) => (
                                                        <td key={colIdx}>
                                                            <Skeleton height={16} />
                                                        </td>
                                                    ))}
                                                </tr>
                                            ))
                                        ) : products.length === 0 ? (
                                            <tr>
                                                <td colSpan={10} className="text-center">
                                                    No data available
                                                </td>
                                            </tr>
                                        ) : (
                                            products.map((p, index) => (
                                                <tr key={p.p_pid || index}>
                                                    <td>{index + 1}</td>
                                                    <td>{p.p_name}</td>
                                                    <td>
                                                        {p.p_catid === "1"
                                                            ? "Ayurvedic"
                                                            : p.p_catid === "2"
                                                                ? "Astrology"
                                                                : p.p_catid}
                                                    </td>
                                                    <td>
                                                        {(p.total_orders || 0).toLocaleString("en-IN")}
                                                    </td>
                                                    <td>
                                                        {(p.total_qty || 0).toLocaleString("en-IN")}
                                                    </td>
                                                    <td>
                                                        ₹ {(p.total_revenue || 0).toLocaleString("en-IN")}
                                                    </td>
                                                    <td>
                                                        ₹ {(p.delivered_revenue || 0).toLocaleString(
                                                            "en-IN"
                                                        )}
                                                    </td>
                                                    <td>
                                                        {(p.order_percent || 0).toFixed(2)}%
                                                    </td>
                                                    <td>
                                                        {(p.delivery_percent || 0).toFixed(2)}%
                                                    </td>
                                                    <td>
                                                        ₹ {(p.avg_ticket || 0).toLocaleString("en-IN")}
                                                    </td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </Table>
                            </div>
                        </div>
                    </div>

                    {/* RIGHT: DONUT CHART */}
                    <div className="col-lg-5 mb-3" style={{ background: "#fff" }}>
                        <div className="h-100 shadow-sm border-0 d-flex justify-content-center" style={{ margin: "auto" }}>
                            {donutData.length > 0 ? (
                                <PieChart width={600} height={320}>
                                    <Pie
                                        data={donutData}
                                        dataKey="value"
                                        nameKey="name"
                                        cx="50%"
                                        cy="50%"
                                        innerRadius={80}
                                        outerRadius={110}
                                        paddingAngle={3}
                                        label={renderDonutLabelWithLine}
                                        labelLine={false}
                                    >
                                        {donutData.map((entry, index) => (
                                            <Cell
                                                key={`cell-${index}`}
                                                fill={DONUT_COLORS[index % DONUT_COLORS.length]}
                                            />
                                        ))}
                                    </Pie>

                                    <text
                                        x="50%"
                                        y="50%"
                                        textAnchor="middle"
                                        dominantBaseline="middle"
                                        fontSize={14}
                                        fontWeight={600}
                                        fill="#333"
                                    >
                                        Order Share
                                    </text>

                                    <Tooltip
                                        formatter={(v, _n, payload) => [
                                            `${Number(v).toFixed(2)}%`,
                                            payload.payload.name,
                                        ]}
                                    />
                                </PieChart>
                            ) : (
                                <div className="p-3">No chart data</div>
                            )}
                        </div>
                    </div>
                </div>
            </ContentWrapper>
        </div>
    );
};

export default withAuth(AllProductReport);
